
import { useState } from 'react';
import { KpiCardType } from '../types';
import { v4 as uuidv4 } from 'uuid';
import { useToast } from '@/hooks/use-toast';

const DEFAULT_CARDS: KpiCardType[] = [
  {
    id: 'stats-1',
    title: 'Stats',
    order: 1,
    visible: true,
    component: null
  },
  {
    id: 'maintenance-2',
    title: 'Maintenance',
    order: 2,
    visible: true,
    component: null
  },
  {
    id: 'content-3',
    title: 'Content',
    order: 3,
    visible: true,
    component: null
  }
];

export const useKpiCardsState = () => {
  const [kpiCards, setKpiCards] = useState<KpiCardType[]>(DEFAULT_CARDS);
  const { toast } = useToast();
  
  const addKpiCard = (card: Partial<KpiCardType>) => {
    // Generate a unique ID with a prefix based on the title
    const prefix = (card.title || 'custom').toLowerCase().replace(/\s+/g, '-');
    const id = `${prefix}-${uuidv4().split('-')[0]}`;
    
    // Find the highest existing order and add 1
    const highestOrder = kpiCards.length > 0 
      ? Math.max(...kpiCards.map(c => c.order)) 
      : 0;
    
    const newCard: KpiCardType = {
      id,
      title: card.title || 'Custom KPI',
      order: highestOrder + 1,
      visible: card.visible ?? true,
      component: card.component || null
    };
    
    setKpiCards(prev => [...prev, newCard]);
    
    toast({
      title: "Card Added",
      description: `Added new card: ${newCard.title}`,
    });
  };
  
  const updateKpiCard = (id: string, updates: Partial<KpiCardType>) => {
    setKpiCards(prev => 
      prev.map(card => 
        card.id === id ? { ...card, ...updates } : card
      )
    );
  };
  
  const removeKpiCard = (id: string) => {
    const cardToRemove = kpiCards.find(card => card.id === id);
    
    if (!cardToRemove) return;
    
    setKpiCards(prev => prev.filter(card => card.id !== id));
    
    toast({
      title: "Card Removed",
      description: `Removed card: ${cardToRemove.title}`,
    });
  };

  return {
    kpiCards,
    setKpiCards,
    addKpiCard,
    updateKpiCard,
    removeKpiCard
  };
};
